import React from 'react';
import './App.css';
import FullscreenSwitch from './UI/FullscreenSwitch';
import LoadingIndicator from './UI/LoadingIndicator';
import CustomUI from './UI/CustomUI';

class App extends React.Component {

	//#region init
	constructor(props) {
		super(props);

		this.appContainerRef = React.createRef();

		//these are not stored in state to avoid unnecessary repaints
		this.dragAmount = 0;
		this.preloadedSets = [];
		this.setsConfigs = {};

		this.config = {
			moveThreshold: 40,
			reverseDirection: false,
			defaultSetIdx: 0,
			availableTypes: ["t1", "t2", "t3", "t4", "t5", "t6"],
			defaultOrientation: 0,
			imagesRatio: 16 / 9,
			panWhenZoomed: false
		};

		this.state = {
			dragging: false,
			setIdx: null,
			viewIdx: 0,
			zoomLevel: 100,
			hasHousings: false,
			typologyFilters: null,
			panOffset: { x: 0, y: 0 }
		};
	}

	componentDidMount = async () => {
		const response = await fetch(`360/config.json`);
		const config = await response.json();
		this.config = { ...this.config, ...config };
		this.config.directionSign = this.config.reverseDirection ? -1 : 1;
		this.initTypologyFilters();
		window.addEventListener('resize', this.handleWindowResized);
		this.handleSetChanged(this.config.defaultSetIdx);
	}

	initTypologyFilters = () => {
		const filters = {};
		this.config.availableTypes.forEach(type => {
			filters[type] = true;
		});
		this.setState({ typologyFilters: filters });
	}
	//#endregion init

	//#region tear down
	componentWillUnmount = () => {
		window.removeEventListener('resize', this.handleWindowResized);
	}
	//#endregion

	//#region drag to rotate / pan
	resetPointerMove = (event) => {
		switch (event.type) {
			case 'mousedown':
				this.lastPointer = { x: event.clientX, y: event.clientY };
				break;
			case 'touchstart':
				this.lastPointer = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
				break;
			default:
				throw new Error(`resetPointerMove() called for ${event.type}, this shouldn't happen`);
		}
	}

	getPointerMove = (event) => {
		let newPointer = null;
		switch (event.type) {
			case 'mousemove':
				newPointer = { x: event.clientX, y: event.clientY };
				break;
			case 'touchmove':
				newPointer = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
				break;
			default:
				throw new Error(`getPointerMove() called for ${event.type}, this shouldn't happen`);
		}
		const move = { x: newPointer.x - this.lastPointer.x, y: newPointer.y - this.lastPointer.y };
		this.lastPointer = newPointer;
		return move;
	}

	handleImgMouseDown = (event) => {
		this.resetPointerMove(event);
		this.dragAmount = 0;
		//we don't wanna trigger a repaint here
		//eslint-disable-next-line
		this.state.dragging = true;
	}

	handleImgMouseUp = () => {
		this.setState({ dragging: false });
	}

	handleImgMouseMove = (event) => {
		if (!this.state.dragging)
			return;

		const move = this.getPointerMove(event);
		if (this.config.panWhenZoomed && this.state.zoomLevel > 100) {
			const newOffset = {
				x: this.state.panOffset.x + 100 * move.x / this.graphicsContainerWidth,
				y: this.state.panOffset.y + 100 * move.y / this.graphicsContainerHeight
			};
			this.constrainPanning(newOffset);
		}
		else {
			this.dragAmount += move.x * this.config.directionSign;
			if (Math.abs(this.dragAmount) < this.config.moveThreshold)
				return;

			let moveSign = Math.sign(this.dragAmount);
			this.dragAmount -= moveSign * this.config.moveThreshold;

			let newView = (this.state.viewIdx + moveSign) % this.config.picturesPerSet;
			while (newView < 0)
				newView += this.config.picturesPerSet;
			this.setState({ viewIdx: newView, selectedHousing: null });
		}
	}

	constrainPanning = (offset) => {
		let maxOffset = (Math.max(this.state.zoomLevel, 100) - 100) / 2;
		if (maxOffset > 0)
			maxOffset += 3; //margin so the picture is not against container side
		offset.x = Math.sign(offset.x) * Math.min(maxOffset, Math.abs(offset.x));
		offset.y = Math.sign(offset.y) * Math.min(maxOffset, Math.abs(offset.y));
		this.setState({ panOffset: offset });
	}
	//#endregion

	//#region zoom
	handleZoomLevelChange = async (newZoomLevel) => {
		await this.setState({ zoomLevel: newZoomLevel });
		this.constrainPanning(this.state.panOffset);
	}
	//#endregion

	//#region changing sets
	handleSetChanged = async (newSetIdx) => {
		if (this.state.setIdx === newSetIdx)
			return;
		await this.setState({ setIdx: null });
		this.preloadSetImages(newSetIdx);
		const hasHousings = await this.loadSetConfig(newSetIdx);
		this.setState({ setIdx: newSetIdx, hasHousings });
	}

	preloadSetImages = (newSetIdx) => {
		if (this.preloadedSets.indexOf(newSetIdx) !== -1)
			return;
		this.preloadedSets.push(newSetIdx);
		for (let i = 0; i < this.config.picturesPerSet; i++) {
			const link = document.createElement('link');
			link.rel = 'preload';
			link.href = `${this.config.sets[newSetIdx].ldPath}/${i}.jpg`;
			link.as = 'image';
			document.head.appendChild(link);
		}
	}

	loadSetConfig = async (newSetIdx) => {
		const set = this.config.sets[newSetIdx];
		if (set.hasHousings === false)
			return false;
		if (this.setsConfigs[set.path] === undefined) {
			const response = await fetch(`${set.path}.json`);
			const setConfig = await response.json();
			this.setsConfigs[set.path] = setConfig;
		}
		return true;
	}
	//#endregion

	//#region selecting housings
	handleHousingClicked = (event) => {
		event.stopPropagation();
		const clickedHousingName = event.target.id;
		if (this.state.selectedHousing === clickedHousingName)
			this.setState({ selectedHousing: null });
		else
			this.setState({ selectedHousing: clickedHousingName });
		return false;
	}

	handleImgClicked = () => {
		this.setState({ selectedHousing: null });
	}

	handleHousingMouseEnter = (event) => {
		this.setState({ hoveredHousing: event.target.id });
	}

	handleHousingMouseLeave = () => {
		this.setState({ hoveredHousing: null });
	}
	//#endregion

	//#region typology filtering
	handleFilterStateToggled = (filterIdx) => {
		const filters = this.state.typologyFilters;
		const type = Object.keys(filters)[filterIdx];
		filters[type] = !filters[type];
		this.setState({ typologyFilters: filters });
	}
	//#endregion

	//#region rendering
	handleWindowResized = () => {
		//force re-render
		this.setState(this.state);
	}

	renderHousingDetailsButton = (housing, classes) => {
		if (housing.name !== this.state.hoveredHousing)
			return null;
		const coords = housing.views[this.state.viewIdx].center.split(" ");
		return (
			<g>
				<circle className={classes} cx={coords[0]} cy={coords[1]} />
				<text x={coords[0]} y={coords[1]}>+</text>
			</g>
		);
	}

	renderHousing = (housing, idx, transform) => {
		if (!this.state.typologyFilters[housing.type])
			return null;
		const classes = housing.type + (this.state.selectedHousing === housing.name ? ' selected' : '');
		return (
			<g key={idx}>
				<path
					transform={transform}
					id={housing.name}
					d={housing.views[this.state.viewIdx].path}
					className={classes}
					onClick={this.handleHousingClicked}
					onMouseEnter={this.handleHousingMouseEnter}
					onMouseLeave={this.handleHousingMouseLeave}
				/>
				{this.renderHousingDetailsButton(housing, classes)}
			</g>

		);
	}

	renderHousings = (scale) => {
		const setPath = this.config.sets[this.state.setIdx].path;
		const setConfig = this.setsConfigs[setPath];
		return (
			<svg id="housings" viewBox={setConfig.viewBox} style={{ transform: scale }}>
				{setConfig.housings.map((h, idx) => this.renderHousing(h, idx, setConfig.transform))}
			</svg>
		);
	}

	renderSet = () => {
		const viewSrc = `${this.config.sets[this.state.setIdx].path}/${this.state.viewIdx}.jpg`;
		const viewSrcLd = `${this.config.sets[this.state.setIdx].ldPath}/${this.state.viewIdx}.jpg`;
		const scale = `scale(${this.state.zoomLevel / 100})`;
		const showHousings = this.state.hasHousings && !this.state.dragging;

		let style = {
			backgroundImage: `url(${viewSrcLd})`,
			backgroundSize: `${this.state.zoomLevel}%`,
			transform: `translate(${this.state.panOffset.x}%, ${this.state.panOffset.y}%)`
		};

		let mainImg = null;
		if (!this.state.dragging || (this.config.panWhenZoomed && this.state.zoomLevel > 100))
			mainImg = <img id="mainImg" alt="" src={viewSrc} style={{ transform: scale }} />;

		return (
			<div>
				<div id="graphicsContainer"
					style={style} >
					{mainImg}
					{showHousings ? this.renderHousings(scale) : null}
				</div>
			</div>
		);
	}

	render() {
		this.graphicsContainerWidth = window.innerWidth;
		this.graphicsContainerHeight = window.innerHeight;
		const windowRatio = this.graphicsContainerWidth / this.graphicsContainerHeight;
		if (windowRatio > this.config.imagesRatio)
			this.graphicsContainerWidth = this.config.imagesRatio * this.graphicsContainerHeight;
		else
			this.graphicsContainerHeight = this.graphicsContainerWidth / this.config.imagesRatio;

		const compassOrientation = this.config.defaultOrientation + this.state.viewIdx * (360 / this.config.picturesPerSet) * this.config.directionSign;

		return (
			<div className="App" ref={this.appContainerRef}
				style={{ width: `${this.graphicsContainerWidth}px`, height: `${this.graphicsContainerHeight}px` }}
				onMouseDown={this.handleImgMouseDown} onTouchStart={this.handleImgMouseDown}
				onMouseMove={this.handleImgMouseMove} onTouchMove={this.handleImgMouseMove}
				onMouseUp={this.handleImgMouseUp} onTouchEnd={this.handleImgMouseUp}
				onMouseLeave={this.handleImgMouseUp} onTouchCancel={this.handleImgMouseUp}
				onClick={this.handleImgClicked} >

				<LoadingIndicator />
				{this.state.setIdx !== null ? this.renderSet() : null}
				<FullscreenSwitch targetRef={this.appContainerRef} />

				<CustomUI
					compassOrientation={compassOrientation}
					selectedSet={this.state.setIdx} onSetSelected={this.handleSetChanged}
					onZoomLevelChanged={this.handleZoomLevelChange}
				/>
			</div>
		);
	}
	//#endregion
}

export default App;
